/**
 * Uma única conexão Socket.IO por sessão (por token).
 * Evita múltiplas conexões quando o usuário navega entre Dashboard e Campanhas.
 */
import React, { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Socket } from 'socket.io-client';

interface SocketContextType {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

function getSocketUrl(): string {
  const isProduction = window.location.protocol === 'https:' || window.location.hostname !== 'localhost';
  if (typeof import.meta.env.VITE_SOCKET_URL !== 'undefined' && import.meta.env.VITE_SOCKET_URL) {
    return import.meta.env.VITE_SOCKET_URL;
  }
  return isProduction ? window.location.origin : `http://${window.location.hostname}:3001`;
}

interface SocketProviderProps {
  children: ReactNode;
  token: string | null;
}

export function SocketProvider({ children, token }: SocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
      return;
    }

    let mounted = true;
    import('socket.io-client').then(({ io }) => {
      if (!mounted) return;
      const socketUrl = getSocketUrl();
      const s = io(socketUrl, {
        auth: { token },
        path: '/api/socket.io',
        transports: ['websocket', 'polling'],
      });
      socketRef.current = s;
      setSocket(s);
      s.on('disconnect', () => setSocket(null));
    });

    return () => {
      mounted = false;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
    };
  }, [token]);

  return (
    <SocketContext.Provider value={{ socket }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket(): Socket | null {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context.socket;
}
